import ShieldLockIcon from '@mdi/svg/svg/shield-lock-outline.svg?react';
import ShieldOffIcon from '@mdi/svg/svg/shield-off-outline.svg?react';
import { MDIIcon } from '@solinkcorp/solink-components';
import { Button, Card, cn } from '@solinkcorp/solink-ui';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { CARD_MAX_WIDTH_CLASS, DESTRUCTIVE_BUTTON_CLASS, PRIMARY_BUTTON_CLASS } from '../constants';
import Keypad from '../Keypad';
import { PIN_LENGTH } from '../useVirtualTabletFlow';

interface StatusKeypadCardProps {
  digits: string;
  errorMessage?: string | null;
  onDigit: (digit: string) => void;
  onClear: () => void;
  onArm: () => void;
  onDisarm: () => void;
}

const StatusKeypadCard: React.FC<StatusKeypadCardProps> = ({
  digits,
  errorMessage,
  onDigit,
  onClear,
  onArm,
  onDisarm,
}) => {
  const locale = useLocale();
  const actionsDisabled = digits.length !== PIN_LENGTH;

  return (
    <Card
      // min-h-0: lets this shrink instead of forcing the page to scroll.
      className={cn(CARD_MAX_WIDTH_CLASS, 'min-h-0 flex-1 gap-0 overflow-hidden')}
    >
      <div className="flex min-h-0 flex-1 flex-col items-center text-center">
        {/* No status claim yet — location hasn't been confirmed. Real state shows on success. */}
        <div className="mb-4 text-foreground-primary heading-display">
          {locale.virtualTablet.enterPinToProceed}
        </div>

        <div className="mb-4 flex gap-6" data-testid="pin-dashes">
          {Array.from({ length: PIN_LENGTH }).map((_, index) => (
            <span
              key={index}
              className={cn(
                'flex h-6 w-6 items-end justify-center border-b-2 pb-0.5 text-foreground-primary heading-lg',
                index < digits.length ? 'border-foreground-primary' : 'border-border-strong',
              )}
            >
              {index < digits.length ? '*' : ''}
            </span>
          ))}
        </div>

        {errorMessage && (
          <p role="alert" className="mb-4 text-text-error caption" data-testid="pin-error-message">
            {errorMessage}
          </p>
        )}

        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
          <Keypad onDigit={onDigit} onClear={onClear} />
        </div>

        <div className="mt-4 flex w-full shrink-0 gap-3">
          <Button
            type="button"
            variant="destructive"
            disabled={actionsDisabled}
            onClick={onArm}
            data-testid="arm-button"
            className={cn('h-12 flex-1', DESTRUCTIVE_BUTTON_CLASS)}
          >
            {/* Same icons as the Alarms page's 24/7 and Off modes. */}
            <MDIIcon icon={ShieldLockIcon} size={16} className="fill-current" />
            {locale.virtualTablet.armButton}
          </Button>
          <Button
            type="button"
            disabled={actionsDisabled}
            onClick={onDisarm}
            data-testid="disarm-button"
            className={cn('h-12 flex-1', PRIMARY_BUTTON_CLASS)}
          >
            <MDIIcon icon={ShieldOffIcon} size={16} className="fill-current" />
            {locale.virtualTablet.disarmButton}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default StatusKeypadCard;
